import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAddSystem() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    system_name: "",
    ein: "",
    aliases: "",
    name_patterns: "",
    seed_ccns: "",
    ownership: "non_profit",
    ein_status: "resolved",
    ein_note: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="border border-red-200 bg-red-50 rounded p-4 text-sm text-red-800" data-testid="admin-required-banner">
          Admin role required. You are signed in as <code>{user.email}</code> with role <code>{user.role}</code>.
        </div>
      </div>
    );
  }

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null); setResult(null);
    const payload = {
      system_name: form.system_name.trim(),
      ein: form.ein.trim(),
      aliases: form.aliases.split(",").map((x) => x.trim()).filter(Boolean),
      name_patterns: form.name_patterns.trim() || null,
      seed_ccns: form.seed_ccns.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean),
      ownership: form.ownership,
      ein_status: form.ein_status,
      ein_note: form.ein_note,
    };
    try {
      const r = await axios.post(`${API}/admin/systems/add`, payload, { withCredentials: true });
      setResult(r.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 mb-2" data-testid="admin-add-system-title">
        Add a parent health system
      </h1>
      <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
        Atomically upserts a row in <code className="text-xs">manual_systems.csv</code>, refreshes
        ProPublica 990 filings for the new EIN, and rebuilds the system → member index. Idempotent
        on EIN.
      </p>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-add-system-form">
        <Field label="System name *" testid="add-system-name">
          <input required value={form.system_name} onChange={onChange("system_name")}
                 className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
                 data-testid="add-system-name-input"
                 placeholder="OhioHealth" />
        </Field>

        <Field label="EIN (9 digits, hyphen allowed) *" testid="add-system-ein">
          <input required value={form.ein} onChange={onChange("ein")}
                 className="w-full px-3 py-2 border border-zinc-300 rounded text-sm font-mono"
                 data-testid="add-system-ein-input"
                 placeholder="32-0007056" />
        </Field>

        <Field label="Aliases (comma-separated)" testid="add-system-aliases"
               hint="Folded into name_patterns regex as escaped alternation.">
          <input value={form.aliases} onChange={onChange("aliases")}
                 className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
                 data-testid="add-system-aliases-input"
                 placeholder="OhioHealth, Ohio Health, OhioHealth Corporation" />
        </Field>

        <Field label="Name patterns (regex, optional)" testid="add-system-name-patterns"
               hint="Validated for compilation before save. Case-insensitive at query time.">
          <input value={form.name_patterns} onChange={onChange("name_patterns")}
                 className="w-full px-3 py-2 border border-zinc-300 rounded text-sm font-mono"
                 data-testid="add-system-name-patterns-input"
                 placeholder="ohio\\s*health|riverside methodist" />
        </Field>

        <Field label="Seed CCNs (comma or whitespace separated)" testid="add-system-seed-ccns"
               hint="Each CCN must exist in hospital_master — validated before write.">
          <textarea rows={3} value={form.seed_ccns} onChange={onChange("seed_ccns")}
                    className="w-full px-3 py-2 border border-zinc-300 rounded text-sm font-mono"
                    data-testid="add-system-seed-ccns-input"
                    placeholder="360006 360017 360152 360348 360210 360011 361315 360014 360170 360118 363037" />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Ownership" testid="add-system-ownership">
            <select value={form.ownership} onChange={onChange("ownership")}
                    className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
                    data-testid="add-system-ownership-select">
              <option value="non_profit">non_profit</option>
              <option value="for_profit">for_profit</option>
              <option value="government_state">government_state</option>
              <option value="government_local">government_local</option>
              <option value="government_federal">government_federal</option>
            </select>
          </Field>
          <Field label="EIN status" testid="add-system-ein-status">
            <select value={form.ein_status} onChange={onChange("ein_status")}
                    className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
                    data-testid="add-system-ein-status-select">
              <option value="resolved">resolved</option>
              <option value="unresolved">unresolved (state hospital, no 990)</option>
            </select>
          </Field>
        </div>

        <Field label="Note (optional, internal)" testid="add-system-note">
          <input value={form.ein_note} onChange={onChange("ein_note")}
                 className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
                 data-testid="add-system-note-input"
                 placeholder="ProPublica-verified parent entity, FY2023 rev $1.88B" />
        </Field>

        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={busy}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded"
                  data-testid="add-system-submit-btn">
            {busy ? "Saving…" : "Add / update system"}
          </button>
          <button type="button" onClick={() => nav("/")}
                  className="px-4 py-2 border border-zinc-300 text-zinc-700 text-sm rounded">
            Cancel
          </button>
        </div>
      </form>

      {err && (
        <div className="mt-6 border border-red-200 bg-red-50 rounded p-3 text-sm text-red-800"
             data-testid="add-system-error">
          <div className="font-semibold mb-1">Error</div>
          <pre className="whitespace-pre-wrap font-mono text-xs">{err}</pre>
        </div>
      )}

      {result && (
        <div className="mt-6 border border-emerald-200 bg-emerald-50 rounded p-4 text-sm"
             data-testid="add-system-result">
          <div className="font-semibold text-emerald-800 mb-2">
            {result.action === "created" ? "Created" : "Updated"}: {result.system_name} (EIN {result.ein})
          </div>
          <ul className="space-y-1 text-zinc-700">
            <li>Members after recompute: <strong data-testid="result-member-count">{result.member_count_after_recompute}</strong></li>
            <li>Trust score: <strong data-testid="result-trust-score">{result.trust_score_after_recompute ?? "—"}</strong></li>
            <li>ProPublica filings collected: <strong>{result.propublica?.filings_collected ?? 0}</strong>{" "}
                {result.propublica?.latest_tax_year && <span className="text-zinc-500">(latest TY {result.propublica.latest_tax_year})</span>}</li>
            <li>Seed CCNs validated: <span className="font-mono text-xs">{(result.seed_ccns || []).join(" ") || "(none)"}</span></li>
          </ul>
          <div className="mt-3">
            <a href={`/systems/${result.ein}`} className="text-emerald-700 underline text-xs"
               data-testid="result-view-system-link">
              View system page →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-600 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </label>
  );
}
